import React, { useMemo } from "react";

import { Grid } from "@material-ui/core";
import { useFormikContext } from "formik";
import { sortBy } from "lodash";
import { useSelector } from "react-redux";
import styled from "styled-components/macro";

import {
  CheckBox,
  DateInput,
  SelectField,
} from "components/Form/FormikControls";

import { businessRelations as businessRelationTypes } from "constants/businesses";

import {
  getBusinessLabels,
  getBusinessRelations,
  getBusinesses,
  getIsLivestockAgent,
} from "selectors";

import { OutputFormatField } from "components/MultiSaleReports/SingleReportForm/OutputFormatField";

import {
  ClientAgingClientType,
  ClientAgingGroupBy,
  ClientAgingLayout,
  ClientAgingOrderBy,
  ClientAgingPeriod,
  OutputFormat,
} from "constants/reports";

// The API takes the relationship key (e.g. "stock_agent"), the store holds the
// display name.
const RelationTypeOptions = sortBy(
  Object.entries(businessRelationTypes).map(([key, relationType]) => ({
    label:
      relationType === businessRelationTypes.OTHER
        ? "Other Relationship"
        : relationType,
    value: key.toLowerCase(),
  })),
  "label",
);

interface GroupByOption {
  label: string;
  value: string;
  agencyOnly?: boolean;
}

const GroupByOptions: GroupByOption[] = [
  { label: "None", value: ClientAgingGroupBy.NONE },
  { label: "Branch", value: ClientAgingGroupBy.BRANCH, agencyOnly: true },
  { label: "Client Label", value: ClientAgingGroupBy.LABEL },
  ...RelationTypeOptions.map(option => ({ ...option, agencyOnly: true })),
];

export const ClientAgingOptions = {
  buckets: [
    { label: "7 Day Bands", value: ClientAgingPeriod.SEVEN_DAYS },
    { label: "30 Day Bands", value: ClientAgingPeriod.THIRTY_DAYS },
    {
      label: "Calendar Months (Xero style)",
      value: ClientAgingPeriod.MONTHLY,
    },
  ],
  bucketCount: [4, 5, 6, 7, 8].map(count => ({
    label: `${count}`,
    value: count,
  })),
  groupBy: GroupByOptions,
  layout: [
    {
      label: "Summary (one line per client)",
      value: ClientAgingLayout.SUMMARY,
    },
    { label: "Detail (list each document)", value: ClientAgingLayout.DETAIL },
  ],
  clientType: [
    {
      label: "Combined (debtors and creditors)",
      value: ClientAgingClientType.COMBINED,
    },
    {
      label: "Debtors only (owed to us)",
      value: ClientAgingClientType.DEBTORS,
    },
    {
      label: "Creditors only (owed by us)",
      value: ClientAgingClientType.CREDITORS,
    },
  ],
  orderBy: [
    { label: "Name", value: ClientAgingOrderBy.NAME },
    { label: "Short Code", value: ClientAgingOrderBy.CODE },
    { label: "Total (largest first)", value: ClientAgingOrderBy.TOTAL },
    { label: "Oldest Amount", value: ClientAgingOrderBy.OLDEST },
  ],
};

export const ClientAgingInitialValues = {
  buckets: ClientAgingPeriod.SEVEN_DAYS,
  bucketCount: 6,
  groupBy: ClientAgingGroupBy.NONE,
  orderBy: ClientAgingOrderBy.NAME,
  layout: ClientAgingLayout.SUMMARY,
  clientType: ClientAgingClientType.COMBINED,
  showContactDetails: false,
};

export const getClientAgingGroupByOptions = (isLivestockAgent: boolean) =>
  ClientAgingOptions.groupBy.filter(
    option => isLivestockAgent || !option.agencyOnly,
  );

const FilterFieldset = styled.fieldset`
  margin: 0;
  padding: ${({ theme }) => theme.space[2]}px;
  border: 1px solid ${({ theme }) => theme.colors.grayAE};
  border-radius: 4px;

  legend {
    padding: 0 ${({ theme }) => theme.space[1]}px;
    color: ${({ theme }) => theme.colors.primary};
    font-size: ${({ theme }) => theme.fontSizes.delta}px;
  }
`;

interface ClientAgingFieldsProps {
  outputFormatOptions: string[];
}

interface ClientAgingFormValues {
  outputFormat?: string;
  relationType?: string | null;
}

export const ClientAgingFields = ({
  outputFormatOptions,
}: ClientAgingFieldsProps): React.JSX.Element => {
  const { setFieldValue, values } = useFormikContext<ClientAgingFormValues>();
  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const businessLabels = useSelector(getBusinessLabels);
  const businesses = useSelector(getBusinesses);
  const relations = useSelector(getBusinessRelations);

  const businessLabelOptions = useMemo(
    () =>
      sortBy(
        Object.values(businessLabels as Record<string, BusinessLabel>).map(
          label => ({
            label: label.name,
            value: label.id,
          }),
        ),
        option => option.label.toLowerCase(),
      ),
    [businessLabels],
  );

  const relationType = values.relationType
    ? businessRelationTypes[values.relationType.toUpperCase()]
    : null;
  const relatedBusinessOptions = useMemo(() => {
    const relatedBusinessIds = new Set(
      Object.values(relations as Record<string, BusinessRelation>)
        .filter(relation => relation.relationType === relationType)
        .map(relation => relation.relatedToId),
    );
    return sortBy(
      [...relatedBusinessIds].map(businessId => ({
        label: businesses[businessId]?.name || "-",
        value: businessId,
      })),
      option => option.label.toLowerCase(),
    );
  }, [businesses, relations, relationType]);

  return (
    <>
      <Grid item xs={6}>
        <SelectField
          label="Scope"
          name="clientType"
          options={ClientAgingOptions.clientType}
        />
      </Grid>
      <Grid item xs={6}>
        <DateInput
          name="asAtDate"
          label="As At Date (blank for today)"
          dataTour="as-at-date"
        />
      </Grid>
      <Grid item xs={12}>
        <FilterFieldset>
          <legend>Filter</legend>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <SelectField
                label="Client Labels"
                name="labelIds"
                options={businessLabelOptions}
                isClearable
                isMulti
              />
            </Grid>
            {isLivestockAgent && (
              <>
                <Grid item xs={6}>
                  <SelectField
                    label="Relationship Type"
                    name="relationType"
                    options={RelationTypeOptions}
                    onChangeExtra={() =>
                      setFieldValue("relatedBusinessId", null)
                    }
                    isClearable
                  />
                </Grid>
                <Grid item xs={6}>
                  <SelectField
                    label="Related Business (blank for any)"
                    name="relatedBusinessId"
                    options={relatedBusinessOptions}
                    disabled={!values.relationType}
                    isClearable
                  />
                </Grid>
              </>
            )}
          </Grid>
        </FilterFieldset>
      </Grid>
      <Grid item xs={6}>
        <SelectField
          label="Layout"
          name="layout"
          options={ClientAgingOptions.layout}
        />
      </Grid>
      <Grid item xs={3}>
        <SelectField
          label="Age Bands"
          name="buckets"
          options={ClientAgingOptions.buckets}
        />
      </Grid>
      <Grid item xs={3}>
        <SelectField
          label="Number Of Bands"
          name="bucketCount"
          options={ClientAgingOptions.bucketCount}
        />
      </Grid>
      <Grid item xs={6}>
        <OutputFormatField options={outputFormatOptions} />
      </Grid>
      <Grid item xs={6}>
        <SelectField
          label="Group By (PDF)"
          name="groupBy"
          options={getClientAgingGroupByOptions(isLivestockAgent)}
          disabled={values.outputFormat !== OutputFormat.PDF}
        />
      </Grid>
      <Grid item xs={6}>
        <SelectField
          label="Order By"
          name="orderBy"
          options={ClientAgingOptions.orderBy}
        />
      </Grid>
      <Grid item xs={6} container alignItems="flex-end">
        <CheckBox name="showContactDetails" label="Include Contact Details" />
      </Grid>
    </>
  );
};
